import React, { useState, useEffect, useRef, useCallback } from 'react'; // 【追加】 useEffect, useRef
import { motion, AnimatePresence, Variants } from 'framer-motion';
// 【追加】Mail(郵便番号)アイコン
import { User, Plus, Save, Mic, RotateCcw, X, ChevronDown, ChevronUp } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { buttonVariants } from '../ui/button';
import { cn } from '../ui/utils';
import { useDashboardStore } from '../../store/dashboardStore';
import { useMobileEdgeSwipeBack } from '../../hooks/useMobileEdgeSwipeBack';
import { Handover } from '../../types';
import {
  normalizeSpeechTranscript,
  isWhisperSilenceHallucination,
  isWhisperTruncatedNegativeTailOnly,
} from '../../lib/speechTranscriptNormalize';
import { transcribeWhisperAudio } from '../../lib/transcribeWhisper';
import { buildWhisperPrompt } from '../../lib/whisperPrompt';
import { insertSpeechErrorReport } from '../../lib/api';

interface HandoverDetailPanelProps {
  isMobile?: boolean;
}

export const HandoverDetailPanel: React.FC<HandoverDetailPanelProps> = ({ isMobile = false }) => {
  const { 
    selectedPatientId, 
    allPatients,
    allHandovers,
    allStaff,
    loadHandovers,
    addHandover,
    updateHandover,
    deleteHandover,
    isDeletingHandover,
    isNavigatingBack,
    navigateBackToSchedulePanel,
    showSettingsDatePanel,
    clinicSpeechTerms,
  } = useDashboardStore();

  // 設定フローから開いた場合は「閲覧のみ」（追加/編集/削除を禁止）
  const isReadOnly = showSettingsDatePanel;
  const widthClass = isReadOnly ? 'panel-col--settings' : 'panel-col--workflow';
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [handoverContent, setHandoverContent] = useState(''); // 【追加】テキストエリアの内容をstateで管理
  /** マイク録音中（Whisper 送信用） */
  const [isListening, setIsListening] = useState(false);
  /** Whisper API 応答待ち */
  const [isTranscribing, setIsTranscribing] = useState(false);
  /** 音声変換後のテキスト（報告ボタン表示用）。次の録音開始・保存・リセットでクリア */
  const [lastTranscribedText, setLastTranscribedText] = useState<string | null>(null);
  const [isReporting, setIsReporting] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaChunksRef = useRef<BlobPart[]>([]);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const insertRangeRef = useRef<{ start: number | null; end: number | null }>({ start: null, end: null });
  const handoverTextareaRef = useRef<HTMLTextAreaElement>(null);
  const editTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPatientInfoExpanded, setIsPatientInfoExpanded] = useState(false); // 【追加】患者情報の展開状態

  // 【追加】編集状態の管理
  const [editingHandoverId, setEditingHandoverId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  // インラインフィードバック
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showDeleteHandoverConfirm, setShowDeleteHandoverConfirm] = useState(false);
  // 音声入力の追記先（新規/編集）を最新状態で参照するためのref
  const editingHandoverIdRef = useRef<string | null>(null);

  useEffect(() => {
    editingHandoverIdRef.current = editingHandoverId;
  }, [editingHandoverId]);

  // フィードバック自動クリア（3秒後）
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 3000);
    return () => clearTimeout(timer);
  }, [feedback]);

  useEffect(() => {
    if (isReadOnly) {
      setIsFormVisible(false);
      setEditingHandoverId(null);
    }
  }, [isReadOnly]);

  useEffect(() => {
    if (!editingHandoverId) setShowDeleteHandoverConfirm(false);
  }, [editingHandoverId]);

  // 【変更】選択された患者の申し送りのみをフィルタリング
  const handovers = allHandovers.filter(h => 
    selectedPatientId ? String(h.patientId) === String(selectedPatientId) : false
  );

  // 【追加】選択された患者が変わったときに申し送りを読み込む
  useEffect(() => {
    if (selectedPatientId) {
      loadHandovers(selectedPatientId);
    }
  }, [selectedPatientId, loadHandovers]);

  const selectedPatient = allPatients.find(p => String(p.id) === String(selectedPatientId));

  const captureInsertRange = useCallback(() => {
    const isEdit = !!editingHandoverIdRef.current;
    const ta = isEdit ? editTextareaRef.current : handoverTextareaRef.current;
    if (ta) {
      insertRangeRef.current = { start: ta.selectionStart, end: ta.selectionEnd };
    } else {
      insertRangeRef.current = { start: null, end: null };
    }
  }, []);

  const appendTranscriptAtCursor = useCallback(
    (raw: string) => {
      const processed = normalizeSpeechTranscript(raw, clinicSpeechTerms);
      const { start, end } = insertRangeRef.current;
      const isEdit = !!editingHandoverIdRef.current;
      if (isEdit) {
        setEditContent((prev) => {
          const s = start ?? prev.length;
          const e = end ?? prev.length;
          return prev.slice(0, s) + processed + prev.slice(e);
        });
      } else {
        setHandoverContent((prev) => {
          const s = start ?? prev.length;
          const e = end ?? prev.length;
          return prev.slice(0, s) + processed + prev.slice(e);
        });
      }
    },
    [clinicSpeechTerms]
  );

  const stopMediaStream = useCallback(() => {
    const stream = mediaStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const handleMicClick = useCallback(async () => {
    if (isReadOnly || isTranscribing) return;

    if (isListening && mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      return;
    }

    setLastTranscribedText(null);
    captureInsertRange();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : '';
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaChunksRef.current = [];
      mr.ondataavailable = (ev) => {
        if (ev.data.size > 0) mediaChunksRef.current.push(ev.data);
      };
      mr.onerror = () => {
        stopMediaStream();
        setIsListening(false);
        setFeedback({ type: 'error', text: '録音中にエラーが発生しました。' });
      };
      mr.onstop = () => {
        stopMediaStream();
        const blob = new Blob(mediaChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        mediaRecorderRef.current = null;
        mediaChunksRef.current = [];
        setIsListening(false);

        if (blob.size < 64) {
          return;
        }

        void (async () => {
          setIsTranscribing(true);
          try {
            const prompt = buildWhisperPrompt(clinicSpeechTerms);
            const result = await transcribeWhisperAudio(blob, prompt);
            if (!result.ok) {
              setFeedback({ type: 'error', text: result.error });
              return;
            }
            const text = result.text.trim();
            if (!text || isWhisperSilenceHallucination(text)) {
              return;
            }
            if (isWhisperTruncatedNegativeTailOnly(text)) {
              setFeedback({
                type: 'error',
                text: '先頭が聞き取れませんでした。文のはじめから、もう一度話してから終了してください。',
              });
              return;
            }
            const processed = normalizeSpeechTranscript(text, clinicSpeechTerms).trim();
            if (!processed) {
              return;
            }
            appendTranscriptAtCursor(text);
            setLastTranscribedText(processed);
          } catch (e) {
            console.error(e);
            setFeedback({ type: 'error', text: '音声の文字起こしに失敗しました。' });
          } finally {
            setIsTranscribing(false);
          }
        })();
      };

      mediaRecorderRef.current = mr;
      mr.start(250);
      setIsListening(true);
    } catch (e) {
      console.error(e);
      setFeedback({ type: 'error', text: 'マイクの利用が許可されていません。' });
      setIsListening(false);
    }
  }, [
    appendTranscriptAtCursor,
    captureInsertRange,
    clinicSpeechTerms,
    isListening,
    isReadOnly,
    isTranscribing,
    stopMediaStream,
  ]);

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        try {
          mediaRecorderRef.current.stop();
        } catch {
          /* noop */
        }
      }
      stopMediaStream();
    };
  }, [stopMediaStream]);

  // 【重要】リセットボタンの処理
  const handleResetClick = () => {
    setHandoverContent('');
    setLastTranscribedText(null);
  };

  // ▼▼▼【変更点 2】保存処理を行う関数を新設 ▼▼▼
  const handleSave = () => {
    // 1. 入力内容が空の場合は処理を中断
    if (!handoverContent.trim()) {
      return;
    }

    // ▼▼▼ ここからが変更点 ▼▼▼

    // 3. まず、フォームを閉じるアニメーションを開始させる
    setIsFormVisible(false);

    // 4. フォームが閉じる(0.3秒) + 1秒待ってからデータベースに保存
    setTimeout(async () => {
      if (selectedPatientId) {
        const result = await addHandover({
          patientId: selectedPatientId,
          content: handoverContent,
        });
        if (result) {
          setFeedback({ type: 'success', text: '申し送りを追加しました' });
        } else {
          setFeedback({ type: 'error', text: '申し送りの追加に失敗しました' });
        }
        // テキストエリアのリセットもここで行う
        setHandoverContent('');
        setLastTranscribedText(null);
      }
    }, 1300); // 300ミリ秒（フォームが閉じる時間） + 1000ミリ秒（1秒の待機時間）
  };
  // ▲▲▲ ここまで変更 ▲▲▲

  // 【追加】編集アイコンのクリック処理
  const handleEditClick = (handover: Handover) => {
    setIsFormVisible(false); // 追加用フォームを閉じる
    setEditingHandoverId(handover.id);
    setEditContent(handover.content);
  };

  // 【追加】編集・削除ロジックの実装
  const handleUpdate = async () => {
    if (!editContent.trim() || !editingHandoverId) {
      return;
    }

    const ok = await updateHandover(editingHandoverId, {
      content: editContent,
    });
    if (ok) {
      setFeedback({ type: 'success', text: '申し送りを更新しました' });
    } else {
      setFeedback({ type: 'error', text: '申し送りの更新に失敗しました' });
    }
    setEditingHandoverId(null);
  };

  const handleDeleteClick = () => {
    setShowDeleteHandoverConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!editingHandoverId) return;
    const id = editingHandoverId;
    setShowDeleteHandoverConfirm(false);
    const ok = await deleteHandover(id);
    if (ok) {
      setFeedback({ type: 'success', text: '申し送りを削除しました' });
    } else {
      setFeedback({ type: 'error', text: '申し送りの削除に失敗しました' });
    }
    setEditingHandoverId(null);
  };

  const handleReset = () => {
    const originalHandover = handovers.find(h => h.id === editingHandoverId);
    if (originalHandover) {
      setEditContent(originalHandover.content);
    }
  };

  const handleClose = () => {
    setEditingHandoverId(null);
  };

  const handoverCardSurfaceClass = 'border-gray-200 bg-gray-50';

  // 【追加】日付フォーマット関数
  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch (e) {
      return dateString; // フォーマット失敗時は元の文字列を返す
    }
  };

  // 【追加】スタッフ名取得関数
  const getStaffName = (staffId: string | null | undefined): string => {
    if (!staffId) return '不明';
    const staff = allStaff.find(s => s.id === staffId);
    return staff ? staff.name : staffId; // 見つからない場合はstaffIdを返す（uuidが表示されるのを防ぐため、本来は見つからないはず）
  };

  const panelVariants: Variants = isMobile 
    ? {
        hidden: { x: '100%', opacity: 1 },
        hiddenBack: { x: '-100%', opacity: 1 },
        visible: { x: 0, opacity: 1 },
        exitBack: {
          x: '100%',
          opacity: 0,
          transition: { duration: 0.35, ease: 'easeOut' },
        },
        exitForward: { x: '-100%' },
      }
    : {
        hidden: { x: '-100%' },  // デスクトップ版は左から
        visible: { x: 0 },
      };

  const handoverSwipeRef = useRef<HTMLDivElement>(null);
  const handleHandoverEdgeBack = useCallback(() => {
    navigateBackToSchedulePanel();
  }, [navigateBackToSchedulePanel]);
  const { swipeStyle: handoverSwipeStyle } = useMobileEdgeSwipeBack(handoverSwipeRef, {
    isMobile: !!isMobile,
    enabled: !!isMobile,
    onNavigateBack: handleHandoverEdgeBack,
  });

  if (!selectedPatient) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center text-gray-500">
          <User size={48} className="mx-auto mb-4" />
          <p>患者を選択してください</p>
        </div>
      </div>
    );
  }



  const content = (
    <div className="h-full flex flex-col bg-white">
      {/* ヘッダー部分 */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {isMobile ? (
            <button
              onClick={() => {
                navigateBackToSchedulePanel();
              }}
              className="text-lg font-semibold text-gray-800 hover:text-gray-600 transition-colors cursor-pointer"
            >
              申し送り
            </button>
          ) : (
            <h2 className="text-lg font-semibold text-gray-800">申し送り</h2>
          )}
        </div>
      </div>
      
      {/* ▼▼▼ 患者基本情報エリアを修正 ▼▼▼ */}
      <div className="p-4 border-b bg-gray-50 text-sm">
        <div className="space-y-1">
          <button
            onClick={() => setIsPatientInfoExpanded(!isPatientInfoExpanded)}
            className="w-full flex items-center justify-between text-left"
          >
            <p className="font-bold text-lg text-gray-900">
              {selectedPatient.name}
              <span className="ml-2 text-sm font-normal text-gray-500">({selectedPatient.nameRomaji})</span>
            </p>
            {isPatientInfoExpanded ? (
              <ChevronUp size={20} className="text-gray-500" />
            ) : (
              <ChevronDown size={20} className="text-gray-500" />
            )}
          </button>
          
          <AnimatePresence initial={false}>
            {isPatientInfoExpanded && (
              <motion.div
                key="patient-info-expanded"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="mt-2 space-y-2 pt-2 border-t border-gray-200">
                  <p className="text-gray-500">患者番号: {selectedPatient.patientNumber || selectedPatient.id}</p>
                  {(selectedPatient.zipcode || selectedPatient.address) && (
                    <div className="text-gray-700">
                      <span>〒{selectedPatient.zipcode || ''} {selectedPatient.address || ''}</span>
                    </div>
                  )}
                  {selectedPatient.patientPhoneNumber && (
                    <p className="text-gray-700">電話番号: {selectedPatient.patientPhoneNumber}</p>
                  )}
                  {selectedPatient.birthDate && (
                    <p className="text-gray-700">生年月日: {selectedPatient.birthDate}</p>
                  )}
                  {selectedPatient.hasGuardian && (
                    <div className="mt-2 p-3 border border-gray-300 rounded-md bg-gray-50">
                      <div className="font-medium text-gray-700 mb-2">保護者情報</div>
                      {selectedPatient.guardianName && (
                        <div className="text-gray-700">
                          {selectedPatient.guardianName}
                          {selectedPatient.guardianRelationship && `（${selectedPatient.guardianRelationship}）`}
                        </div>
                      )}
                      {selectedPatient.guardianNameRomaji && (
                        <div className="text-gray-700">{selectedPatient.guardianNameRomaji}</div>
                      )}
                      {selectedPatient.guardianContact && (
                        <div className="text-gray-700">{selectedPatient.guardianContact}</div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      {/* ▲▲▲ ここまで修正 ▲▲▲ */}

                   {/* 申し送り一覧 */}
      <div className={`flex-1 overflow-y-auto ${isMobile ? 'pb-[var(--bottom-menu-total)]' : ''}`}>
        <div className="p-4">
          {/* ▼▼▼ 追加（書き込み）は診療フローのみ ▼▼▼ */}
          {!isReadOnly && (
            <motion.button
              onClick={() => {
                setEditingHandoverId(null); // 既存カード（編集フォーム）を閉じる
                setIsFormVisible(!isFormVisible); // 【修正】クリックでフォームの表示/非表示を切り替え
              }}
              className="w-full p-4 rounded-lg border border-dashed border-green-200 bg-green-50 hover:bg-green-100 text-green-600 transition-colors mb-4"
            >
              <div className="flex items-center justify-center space-x-3">
                <Plus size={20} />
                <span className="font-medium">申し送りを追加する</span>
              </div>
            </motion.button>
          )}

          {/* ▼▼▼ 【重要】ここから入力フォームを追加 ▼▼▼ */}
          <AnimatePresence>
            {!isReadOnly && isFormVisible && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                                 transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden mb-4"
              >
                                                                  <div className="p-4 border rounded-lg bg-white relative">
                   <div className="space-y-4">
                                           {/* 閉じるボタンを右上に配置 */}
                      <button 
                        onClick={() => setIsFormVisible(false)} // 閉じる処理
                        className="absolute top-2 right-2 p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors z-10"
                        aria-label="閉じる"
                      >
                        <X size={16} />
                      </button>
                     <div>
                       <label htmlFor="handoverContent" className="text-sm font-medium text-gray-700">内容</label>
                       <textarea
                         ref={handoverTextareaRef}
                         id="handoverContent"
                         rows={4}
                         value={handoverContent} // valueを設定
                         onChange={(e) => setHandoverContent(e.target.value)} // onChangeを設定
                         className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                         placeholder="申し送り内容を入力"
                       />
                     </div>
                     <div className="flex justify-start items-center pt-2">
                       {/* 音声入力・保存・リセットボタン */}
                       <div className="flex w-full gap-2">
                         {/* 音声入力ボタン */}
                         <button 
                           type="button"
                           onClick={() => void handleMicClick()}
                           disabled={isReadOnly || isTranscribing}
                           className={`flex-1 py-3 rounded-md flex flex-col items-center justify-center font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                             isTranscribing
                               ? 'bg-blue-600 text-white animate-pulse'
                               : isListening
                                 ? 'bg-red-500 text-white animate-pulse'
                                 : 'bg-blue-600 text-white hover:bg-blue-700'
                           }`}
                         >
                           <Mic size={20} className="mb-1" />
                           <span className="text-xs">
                             {isTranscribing ? '変換中…' : isListening ? '録音中' : '音声入力'}
                           </span>
                         </button>
                         {/* 保存ボタン */}
                         <button 
                           type="button"
                           onClick={handleSave}
                           className={cn(
                             buttonVariants({ variant: 'cta', size: 'handoverTile' }),
                             'flex flex-col items-center justify-center'
                           )}
                         >
                           <Save size={20} className="mb-1" />
                           <span className="text-xs">保存</span>
                         </button>
                         {/* リセットボタン */}
                         <button 
                           type="button"
                           onClick={handleResetClick} // リセット処理
                           className={cn(
                             buttonVariants({ variant: 'ctaMuted', size: 'handoverTile' }),
                             'flex flex-col items-center justify-center'
                           )}
                         >
                           <RotateCcw size={20} className="mb-1" />
                           <span className="text-xs">リセット</span>
                         </button>
                       </div>
                     </div>
                     {/* 誤変換報告ボタン（音声変換後のみ表示） */}
                     {lastTranscribedText && (
                       <button
                         type="button"
                         disabled={isReporting}
                         onClick={async () => {
                           const { currentClinicId, currentUser } = useDashboardStore.getState();
                           if (!currentClinicId || !lastTranscribedText) return;
                           setIsReporting(true);
                           const ok = await insertSpeechErrorReport({
                             clinicId: currentClinicId,
                             reportedByStaffId: currentUser?.id ?? null,
                             transcribedText: lastTranscribedText,
                           });
                           setIsReporting(false);
                           if (ok) {
                             setLastTranscribedText(null);
                             setFeedback({ type: 'success', text: '誤変換を報告しました' });
                           } else {
                             setFeedback({ type: 'error', text: '報告の送信に失敗しました' });
                           }
                         }}
                         className="mt-2 w-full py-2 rounded-md border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 text-sm font-medium transition-colors disabled:opacity-50"
                       >
                         {isReporting ? '送信中…' : '音声入力の誤変換を報告する'}
                       </button>
                     )}
                   </div>
                 </div>
              </motion.div>
            )}
          </AnimatePresence>
          {/* ▲▲▲ ここまで入力フォーム ▲▲▲ */}
          {/* ▲▲▲ ここまで追加 ▲▲▲ */}

          {feedback && (
            <div className={`mb-3 text-sm px-3 py-2 rounded-md border ${
              feedback.type === 'success'
                ? 'text-green-700 bg-green-50 border-green-200'
                : 'text-red-600 bg-red-50 border-red-200'
            }`}>
              {feedback.text}
            </div>
          )}
                    <h3 className="text-base font-semibold text-gray-800 mb-4">申し送り一覧</h3>
          <div className="space-y-4">
            {/* ▼▼▼【変更点 1】リスト全体を AnimatePresence で囲む ▼▼▼ */}
            <AnimatePresence>
              {handovers.map((handover) => (
                <motion.div
                  key={handover.id}
                  // ▼▼▼【変更点 2】アニメーションの定義を修正・追加 ▼▼▼
                  layout // 他の要素がスムーズに移動するようにする
                  initial={{ opacity: 0, y: -20, scale: 0.9 }} // 上からフワッと現れるための初期状態
                  animate={{ opacity: 1, y: 0, scale: 1 }} // 表示されるときの最終状態
                  exit={{ opacity: 0 }} // 要素が消えるときのアニメーション
                  transition={{ duration: 0.3, ease: 'easeOut' }} // アニメーションの速度と緩急
                  // ▲▲▲ ここまで変更 ▲▲▲
                  onClick={
                    isReadOnly
                      ? undefined
                      : handover.id === editingHandoverId
                        ? undefined
                        : () => handleEditClick(handover)
                  }
                  className={`p-4 rounded-lg border transition-shadow ${
                    handover.id === editingHandoverId
                      ? `relative ${handoverCardSurfaceClass}`
                      : `${isReadOnly ? '' : 'cursor-pointer hover:shadow-md'} ${handoverCardSurfaceClass}`
                  }`}
                >
                  {handover.id === editingHandoverId ? (
                    // 編集フォーム
                    <div className="space-y-4">
                      {/* 閉じるボタンを右上に配置 */}
                      <button 
                        onClick={handleClose}
                        className="absolute top-2 right-2 p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded transition-colors z-10"
                        aria-label="閉じる"
                      >
                        <X size={16} />
                      </button>
                       <div>
                         <label htmlFor="editContent" className="text-sm font-medium text-gray-700">内容</label>
                         <textarea
                           ref={editTextareaRef}
                           id="editContent"
                           rows={4}
                           value={editContent}
                           onChange={(e) => setEditContent(e.target.value)}
                           className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                           placeholder="申し送り内容を入力"
                         />
                       </div>
                       {/* 作成者と記録日を表示 */}
                       <div className="flex flex-col space-y-1 text-xs text-gray-500 pt-2">
                         <div>
                          作成者：{handover.createdByName || getStaffName(handover.createdBy)}
                         </div>
                         <div>
                          記録日：{formatDateTime(handover.createdAt)}
                         </div>
                       </div>
                       <div className="flex justify-start items-center pt-2">
                         <div className="flex w-full gap-2">
                           {/* 音声入力ボタン */}
                           <button 
                             type="button"
                             onClick={() => void handleMicClick()}
                             disabled={isReadOnly || isTranscribing}
                             className={`flex-1 py-3 rounded-md flex flex-col items-center justify-center font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                               isTranscribing
                                 ? 'bg-blue-600 text-white animate-pulse'
                                 : isListening
                                   ? 'bg-red-500 text-white animate-pulse'
                                   : 'bg-blue-600 text-white hover:bg-blue-700'
                             }`}
                           >
                             <Mic size={20} className="mb-1" />
                             <span className="text-xs">
                               {isTranscribing ? '変換中…' : isListening ? '録音中' : '音声入力'}
                             </span>
                           </button>
                           {/* 保存ボタン */}
                           <button 
                             type="button"
                             onClick={handleUpdate}
                             className={cn(
                               buttonVariants({ variant: 'cta', size: 'handoverTile' }),
                               'flex flex-col items-center justify-center'
                             )}
                           >
                             <Save size={20} className="mb-1" />
                             <span className="text-xs">保存</span>
                           </button>
                           {/* リセットボタン */}
                           <button 
                             type="button"
                             onClick={handleReset}
                             className={cn(
                               buttonVariants({ variant: 'ctaMuted', size: 'handoverTile' }),
                               'flex flex-col items-center justify-center'
                             )}
                           >
                             <RotateCcw size={20} className="mb-1" />
                             <span className="text-xs">リセット</span>
                           </button>
                           {/* 削除ボタン */}
                           <button 
                             type="button"
                             onClick={handleDeleteClick}
                             className="flex-1 py-3 rounded-md bg-red-600 text-white hover:bg-red-700 flex flex-col items-center justify-center font-medium"
                           >
                             <X size={20} className="mb-1" />
                             <span className="text-xs">削除</span>
                           </button>
                         </div>
                       </div>
                    </div>
                  ) : (
                    // 通常表示
                    <>
                      <p className="text-black mb-2">{handover.content}</p>
                      <div className="flex flex-col space-y-1 text-xs text-gray-500">
                        <div>
                          作成者：{handover.createdByName || getStaffName(handover.createdBy)}
                        </div>
                        <div>
                          記録日：{formatDateTime(handover.createdAt)}
                        </div>
                      </div>
                    </>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
            {/* ▲▲▲ ここまで変更 ▲▲▲ */}
            </div>
        </div>
      </div>
      
      {/* ▼▼▼ フッター部分を削除 ▼▼▼ */}
      {/* <div className="p-4 border-t"> ... </div> */}
      {/* ▲▲▲ ここまで削除 ▲▲▲ */}

      <AlertDialog open={showDeleteHandoverConfirm} onOpenChange={setShowDeleteHandoverConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>本当に削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const h = handovers.find((x) => x.id === editingHandoverId);
                const raw = (h?.content ?? editContent).replace(/\s+/g, ' ').trim();
                const snippet = raw.length > 100 ? `${raw.slice(0, 100)}…` : raw;
                return snippet
                  ? `「${snippet}」の申し送りを削除します。この操作は元に戻せません。`
                  : 'この申し送りを削除します。この操作は元に戻せません。';
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingHandover}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleDeleteConfirm();
              }}
              disabled={isDeletingHandover}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              削除する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  if (isMobile) {
    return (
      <div
        ref={handoverSwipeRef}
        style={handoverSwipeStyle}
        className="fixed top-0 left-0 right-0 bottom-0 pt-[var(--header-actual,var(--header-total))] z-[43]"
      >
        <motion.div
          variants={panelVariants}
          initial={isNavigatingBack ? "hiddenBack" : "hidden"}
          animate="visible"
          exit={isNavigatingBack ? "exitBack" : "exitForward"}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="h-full w-full bg-white flex flex-col shadow-lg"
        >
          {content}
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      variants={panelVariants}
      initial="hidden"
      animate="visible"
      transition={{ duration: 0.3, ease: 'easeOut' }}
      // ▼▼▼ 【重要】z-index はスケジュール（z-[5]）より低く z-0 に設定 ▼▼▼
      className={`panel-col ${widthClass} h-full bg-white border-r z-0`}
    >
      {content}
    </motion.div>
  );
};

